import React from 'react'
import _ from 'underscore'
import styled from 'styled-components'
import ColumnGridPanel from 'components/ColumnGridPanel'
import ErrorMessage from 'components/ErrorMessage'

import FurnaceReport from 'pages/Device/SystemStatusPanel/FurnaceReport'
import {
  defaultIfOutrageous,
  friendlyName,
  onOff,
  cubicFeetPerMinute,
  degreesFahrenheit,
  staticPressure,
  percentage,
  precision,
  rpm,
  voltageAC
} from 'helpers/formatters'
import { FRIENDLY_NAMES } from 'helpers/reportFriendlyNames'
import { IndoorReport } from 'types/SystemStatusReport'

type Props = {
  report: IndoorReport
}

const MCB_SENSOR_STATUS = {
  MONITORING: 'A2L sensor is monitoring the concentration of regrigerant.',
  MITIGATING: 'A2L sensor is detecting a high concentration of refrigerant.',
  SENSOR_ERROR: 'A2L sensor is experiencing an internal error and not sensing refrigerant correctly.',
  UNKNOWN: 'A2L sensor status is unknown.'
}

const IndoorUnit = ({ report }: Props) => {
  const anyFurnace = report.hasS9ModelFurnace || report.hasS8ModelFurnace
  const hasMcbInstalled = report.mcbInstalled

  const indoorReport = {
    extendedIndoorMotorAirFlow: cubicFeetPerMinute(report.extendedIndoorMotorAirFlow),
    extendedIndoorTargetAirflow: anyFurnace ? cubicFeetPerMinute(report.extendedIndoorTargetAirflow) : null,
    extendedIndoorStaticPressure: staticPressure(report.extendedIndoorMotorStaticPressure),
    extendedIndoorInducerSpeedActual: rpm(report.extendedIndoorInducerSpeedActual),
    extendedIndoorCombustionPressureActual: staticPressure(report.extendedIndoorCombustionPressureActual),
    extendedIndoorCombustionPressureTarget: staticPressure(report.extendedIndoorCombustionPressureTarget),
    extendedIndoorGasValvePositionActual: percentage(report.extendedIndoorGasValvePositionActual, 1),
    extendedIndoorGasValvePositionSetpoint: percentage(report.extendedIndoorGasValvePositionSetpoint, 1),
    extendedIndoorTemperatureRise: degreesFahrenheit(report.extendedIndoorTemperatureRise),
    extendedIndoorTemperatureRiseTarget: degreesFahrenheit(report.extendedIndoorTemperatureRiseTarget),
    extendedIndoorLineVoltage: voltageAC(report.extendedIndoorLineVoltage),
    requestedModulationCapacity: percentage(report.requestedModulationCapacity, 1),
    extendedIndoorMotorStaticPressure: staticPressure(report.extendedIndoorMotorStaticPressure),
    extendedIndoorCoilTemp: !anyFurnace ? degreesFahrenheit(report.extendedIndoorCoilTemp) : null,
    extendedIndoorGasTemp: !anyFurnace ? degreesFahrenheit(report.extendedIndoorGasTemp) : null,
    extendedIndoorGasSuperheat: (!anyFurnace && report.operatingStatus === 'SYSTEM_COOLING') ? degreesFahrenheit(report.extendedIndoorGasSuperheat) : null,
    extendedIndoorEevStepPosition: (!anyFurnace && report.deviceType === 'control_hub') ? precision(report.extendedIndoorEevStepPosition, 0) : null,
    dischargeAirTemperature: (report.deviceType === 'control_hub' && report.hasIndoorSupplyAirTempSensorEnabled) ? degreesFahrenheit(report.dischargeAirTemperature) : null,
    returnAirTemperature: (report.deviceType === 'control_hub' && report.hasIndoorReturnAirTempSensorEnabled) ? degreesFahrenheit(report.returnAirTemperature) : null,
    extendedIndoorSupplyStaticPressure: staticPressure(report.extendedIndoorSupplyStaticPressure),
    extendedIndoorReturnStaticPressure: report.hasIndoorReturnStaticSensorEnabled ? staticPressure(report.extendedIndoorReturnStaticPressure) : null,
    relativeHumidity: (!_.isEmpty(report.zones) && report.zones.length > 1) ? percentage(report.relativeHumidity, 1) : null
  }

  const indoorFurnaceReport = {
    extendedIndoorFurnaceDemandStatus: friendlyName(report.extendedIndoorFurnaceDemandStatus, FRIENDLY_NAMES),
    extendedIndoorFurnaceOperationMode: friendlyName(report.extendedIndoorFurnaceOperationMode, FRIENDLY_NAMES),
    extendedIndoorFurnaceInducerErrorHandling: defaultIfOutrageous(report.extendedIndoorFurnaceInducerErrorHandling),
    extendedIndoorFurnaceUnitOrientation: friendlyName(report.extendedIndoorFurnaceUnitOrientation, FRIENDLY_NAMES),
    extendedIndoorFurnaceInducerStage: friendlyName(report.extendedIndoorFurnaceInducerStage, FRIENDLY_NAMES),
    extendedIndoorFurnaceGasValveStatus: friendlyName(report.extendedIndoorFurnaceGasValveStatus, FRIENDLY_NAMES),
    pressureSwitchOne: onOff(report.extendedIndoorFurnaceSwitchStatus?.pressureSwitchOne),
    pressureSwitchTwo: onOff(report.extendedIndoorFurnaceSwitchStatus?.pressureSwitchTwo),
    highLimitStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.highLimitStatus),
    inducerLimitStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.inducerLimitStatus),
    condensateStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.condensateStatus),
    ignitorStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.ignitorStatus),
    rolloutLimitInputStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.rolloutLimitInputStatus),
    reverseAirflowStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.reverseAirflowStatus),
    flameStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.flameStatus),
    accessoryOneStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.accessoryOneStatus),
    accessoryTwoStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.accessoryTwoStatus)
  }

  const indoorMcbReport = {
    mcbSensor1Status: report.mcbSensor1Status,
    mcbSensor2Status: report.mcbSensor2Status
  }

  const hasData = Object.values(indoorReport).some(val => val !== null)
  const hasFurnaceData = anyFurnace && Object.values(indoorFurnaceReport).some(val => val !== null)

  if (!hasData && !hasFurnaceData && !hasMcbInstalled) {
    return null
  }

  const showMcbSensors = (label, value) => {
    const tooltipText =
    value === 'NOT_CONFIGURED'
      ? `${label} is not configured on the system.`
      : MCB_SENSOR_STATUS[value] || ''

    const valueText = value === 'MITIGATING' ? <S.ErrorMessage errorMessage={value} /> : friendlyName(value)

    return (
      <ColumnGridPanel.Column.Item
        key={label}
        tooltip={tooltipText}
        label={label}
        value={valueText}
        hideIfValueIsNull
      />
    )
  }

  return (
    <ColumnGridPanel.Column>
      <ColumnGridPanel.Column.Header>Indoor Unit</ColumnGridPanel.Column.Header>
      <ColumnGridPanel.Column.Item label="Actual Airflow" value={indoorReport.extendedIndoorMotorAirFlow} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Target Airflow" value={indoorReport.extendedIndoorTargetAirflow} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Total Static Pressure" value={indoorReport.extendedIndoorMotorStaticPressure} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Actual Inducer Speed" value={indoorReport.extendedIndoorInducerSpeedActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Actual Combustion Pressure" value={indoorReport.extendedIndoorCombustionPressureActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Combustion Pressure" value={indoorReport.extendedIndoorCombustionPressureTarget} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Actual Gas Valve Stepper Position" value={indoorReport.extendedIndoorGasValvePositionActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Gas Valve Setpoint" value={indoorReport.extendedIndoorGasValvePositionSetpoint} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Temperature Rise - Measured" value={indoorReport.extendedIndoorTemperatureRise} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Temperature Rise" value={indoorReport.extendedIndoorTemperatureRiseTarget} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Line Voltage" value={indoorReport.extendedIndoorLineVoltage} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Demand" value={indoorReport.requestedModulationCapacity} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Evap/Liquid Temp" value={indoorReport.extendedIndoorCoilTemp} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Gas Temp" value={indoorReport.extendedIndoorGasTemp} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="ID Superheat" value={indoorReport.extendedIndoorGasSuperheat} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="ID EEV Steps" value={indoorReport.extendedIndoorEevStepPosition} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="ID Supply Air Temp" value={indoorReport.dischargeAirTemperature} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="ID Return Air Temp" value={indoorReport.returnAirTemperature} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Supply Static Pressure" value={indoorReport.extendedIndoorSupplyStaticPressure} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Return Static Pressure" value={indoorReport.extendedIndoorReturnStaticPressure} hideIfValueIsNull />

      {hasMcbInstalled && ['A2L Sensor 1', 'A2L Sensor 2'].map((label, index) =>
        showMcbSensors(label, indoorMcbReport[`mcbSensor${index + 1}Status`])
      )}

      {anyFurnace && <FurnaceReport report={indoorFurnaceReport} />}

      <ColumnGridPanel.Column.Item label="Indoor Rh" value={indoorReport.relativeHumidity} hideIfValueIsNull />
    </ColumnGridPanel.Column>
  )
}

export default IndoorUnit

const S = {
  ErrorMessage: styled(ErrorMessage)`
    float: left;
  `
}
